import { Component, Input, OnInit, OnDestroy, ViewChildren, QueryList, EventEmitter } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { DialogButton, PaginationPageEvent } from '@snapon/design-system-angular';
import { AppInfoService } from 'core/app-info/app-info.service';
import { currencyCodeSymbols } from 'core/app-info/country-specifics.enum';
import { NotificationService } from 'core/notification/notification.service';
import { NotificationType } from 'core/notification/notification-config.class';
import { SignalRService } from 'core/signalR-service/signal-r.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { AppLocalization } from '../../shared/entities/app-localization';
import { SignalrProgressState } from '../../shared/entities/signalrProgressState.enum';
import { B2BUserInfoColumn, B2BUser, B2BUsersPayload, B2BUsersResponse, RolesResponse } from './entities/b2b-user-info';
import { B2bUserCartComponent } from './b2b-user-cart/b2b-user-cart.component';
import { B2bUserCartService } from './services/b2b-user-cart.service';
import { CartItem } from './entities/cart-item';

@Component({
  selector: 'app-b2b-users',
  templateUrl: './b2b-users.component.html',
  styleUrls: ['./b2b-users.component.scss'],
  standalone: false
})
export class B2bUsersComponent implements OnInit, OnDestroy {
  userInfoColumns: B2BUserInfoColumn[] = [
    { label: 'Items in Cart', key: 'itemsInCart' },
    { label: 'Email', key: 'Email' },
    { label: 'B2B Role', key: 'RoleDescription' },
    { label: 'User Name', key: 'UserName' },
    { label: 'Phone Number', key: 'Phone' },
    { label: 'Cart Subtotal', key: 'Subtotal' }
  ];

  @Input() bpNumber: string;
  @Input() countryCodeId: number;
  @ViewChildren(B2bUserCartComponent) cartComponents: QueryList<B2bUserCartComponent>;
  deleteCartDialogOpen$ = new EventEmitter<boolean>();
  generateQuoteDialogOpen$ = new EventEmitter<boolean>();
  subs = new Subscription();

  appStrings: AppLocalization;
  loading: boolean;
  generateQuoteLoading: boolean;
  currentPage = 1;
  pageSize = 10;
  pageOffset = 0;
  totalPages = 0;
  totalRecords = 0;
  currentPageItemCount = 0;
  users: B2BUser[] = [];
  rolesMap = new Map<string, string>();
  customerId: string;
  deleteDialogButtons: DialogButton[] = [];
  generateQuoteDialogButtons: DialogButton[] = [];
  cartToDelete: B2BUser;
  generatedQuoteId: string;
  userCartItems: Map<number, CartItem[]> = new Map();
  private quoteGenerationContext: { user: B2BUser, userIndex: number, deleteCart: boolean } | null = null;
  isMobile: boolean;

  constructor(
    private b2bUserCartService: B2bUserCartService,
    private appInfoService: AppInfoService,
    private notificationService: NotificationService,
    private router: Router,
    private route: ActivatedRoute,
    private signalrService: SignalRService,
    private deviceService: DeviceDetectorService
  ) { }

  ngOnInit(): void {
    this.isMobile = this.deviceService.isMobile();
    this.loading = true;
    this.appStrings = this.appInfoService.appStrings;
    this.customerId = this.route.snapshot.paramMap.get('customerId');
    this.initializeDialogButtons();
    this.initializeGenerateQuoteDialogButtons();
    this.loadRoles();

    // Subscribe to SignalR generate quote progress
    this.subs.add(
      this.signalrService.generateQuoteProgressReceived$.subscribe(progress => {
        this.handleGenerateQuoteProgress(progress);
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadRoles(): void {
    this.subs.add(this.b2bUserCartService.getRoles().subscribe({
      next: (rolesResponse: RolesResponse) => {
        if (rolesResponse?.Data) {
          this.rolesMap = new Map(
            rolesResponse.Data.map(role => [role.RoleName, role.Description])
          );
        }
        this.getB2BUsers();
      }
    }));
  }

  private getB2BUsers(): void {
    this.loading = true;
    const payload: B2BUsersPayload = {
      shipToBp: this.bpNumber,
      pageNumber: this.currentPage,
      rowsPerPage: this.pageSize
    };

    this.subs.add(this.b2bUserCartService.getB2BUsers(payload).subscribe({
      next: (response: B2BUsersResponse) => {
        this.processUsers(response);
        this.loading = false;
      }
    }));
  }

  private processUsers(response: B2BUsersResponse): void {
    if (response?.Data) {
      this.users = (response.Data.Users ?? []).map(user => ({
        ...user,
        itemsInCart: user.ShoppingCarts?.[0]?.CartItemCount ?? 0,
        Subtotal: user.ShoppingCarts?.[0]?.Subtotal ?? 0,
        RoleDescription: this.rolesMap.get(user.RoleName) ?? ''
      }));

      this.currentPageItemCount = this.users?.length ?? 0;
      this.totalRecords = response.Data.TotalRowCount ?? 0;
      this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
    }
  }

  loadPage(event: PaginationPageEvent): void {
    if (event.pageNumber !== this.currentPage) {
      this.currentPage = event.pageNumber;
      this.pageOffset = (event.pageNumber - 1) * this.pageSize;
      this.getB2BUsers();
    }
  }

  onPanelOpened(userIndex: number): void {
    const cartComponent = this.cartComponents.toArray()[userIndex];
    if (cartComponent) {
      cartComponent.loadCartItems();
    }
  }

  onCartItemsLoaded(event: { userIndex: number, cartItems: CartItem[] }): void {
    this.userCartItems.set(event.userIndex, event.cartItems);
  }

  getUserCartItems(userIndex: number): CartItem[] {
    return this.userCartItems.get(userIndex) ?? [];
  }
  getUserCurrencySymbol(user: B2BUser): string {
    const currencyCode = user.ShoppingCarts?.[0]?.Currency;
    return currencyCodeSymbols[currencyCode];
  }
  navigateToEditCart(user: B2BUser): void {
    this.router.navigate(
      ['/accounts', this.customerId, 'b2b-cart'],
      {state: { userId: user.UserId, shipToBPs: this.bpNumber, countryCodeId: this.countryCodeId,
        userData: { UserName: user.UserName, Email: user.Email, FirstName: user.FirstName, LastName: user.LastName }}}
    );
  }

  private initializeDialogButtons(): void {
    this.deleteDialogButtons = [
      {
        type: 'secondary',
        label: 'Delete B2B Cart',
        onclick: () => this.confirmDeleteCart()
      },
      {
        type: 'primary',
        label: 'Keep B2B Cart',
        onclick: () => this.closeDeleteDialog()
      }
    ];
  }

  showDeleteCartDialog(user: B2BUser): void {
    this.cartToDelete = user;
    this.deleteCartDialogOpen$.emit(true);
  }

  closeDeleteDialog(): void {
    this.cartToDelete = null;
  }

  confirmDeleteCart(): void {
    if (this.cartToDelete) {
      this.deleteUserCart(this.cartToDelete.UserId.toString(), () => {
        this.notificationService.showNotification(this.appStrings.b2bCart_deleteCartSuccess, NotificationType.Success);
        this.cartToDelete = null;
        this.getB2BUsers();
      });
    }
  }

  private deleteUserCart(userId: string, onSuccess?: () => void): void {
    this.loading = true;
    this.subs.add(
      this.b2bUserCartService.deleteCart(userId).subscribe({
        next: () => {
          this.loading = false;
          if (onSuccess) {
            onSuccess();
          }
        },
        error: () => {
          this.loading = false;
        }
      })
    );
  }

  deleteCart(user: B2BUser, event): void {
    event.stopPropagation();
    this.showDeleteCartDialog(user);
  }

  generateQuote(user: B2BUser, event, userIndex: number): void {
    event.stopPropagation();
    this.quoteGenerationContext = { user, userIndex, deleteCart: false };
    this.generateQuoteDialogOpen$.emit(true);
  }

  private startQuoteGeneration(deleteCart: boolean): void {
    if (!this.quoteGenerationContext) {
      return;
    };

    this.quoteGenerationContext.deleteCart = deleteCart;
    this.generateQuoteLoading = true;

    const cartComponent = this.cartComponents.toArray()[this.quoteGenerationContext.userIndex];
    cartComponent.generateQuote();
  }

  private handleGenerateQuoteProgress(progress: any): void {
    if (progress.state === SignalrProgressState.Done) {
      this.generateQuoteLoading = false;
      this.signalrService.closeConnection();
      this.notificationService.showNotification(
        progress.message.actionMessage,
        NotificationType.Success
      );

      if (progress.message.entityId) {
        this.generatedQuoteId = progress.message.entityId;
        if (this.quoteGenerationContext.deleteCart) {
          this.deleteUserCart(this.quoteGenerationContext.user.UserId.toString(), () => {
            this.navigateToQuote();
            this.quoteGenerationContext = null;
          });
        } else {
          this.navigateToQuote();
          this.quoteGenerationContext = null;
        }
      }
    }

    if (progress.state === SignalrProgressState.Error) {
      this.generateQuoteLoading = false;
      this.signalrService.closeConnection();
      this.notificationService.showNotification(
        this.appStrings.quoteGeneration_errorMessage,
        NotificationType.Error
      );
      this.quoteGenerationContext = null;
    }
  }

  private initializeGenerateQuoteDialogButtons(): void {
    this.generateQuoteDialogButtons = [
      {
        type: 'tertiary',
        label: 'Cancel',
        onclick: () => {
          this.quoteGenerationContext = null;
        }
      },
      {
        type: 'secondary',
        label: 'Delete B2B Cart',
        onclick: () => this.startQuoteGeneration(true)
      },
      {
        type: 'primary',
        label: 'Keep B2B Cart',
        onclick: () => this.startQuoteGeneration(false)
      }
    ];
  }

  private navigateToQuote(): void {
    if (this.generatedQuoteId) {
      setTimeout(() => {
        this.router.navigate(['/quotes', this.generatedQuoteId]);
      });
    }
  }
}
